import React, { useEffect, useMemo, useState } from 'react';
import { addDays, max, startOfDay } from 'date-fns';
import { ButtonV2, CloseButton, DatePickerInput, DialogV2, DrawerV2, Flex, FormControl, TextV2, useToast } from '@withjoy/joykit';
import { WarningTriangle } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { createDateFromUnformattedString, getLocaleDateString, isEventDateOutsideStayRange } from '@shared/utils/dateHelpers';
import { useUpdateTravelSettingsMutation } from '@graphql/mutations/travelSettings.graphql.generated';
import { addError } from '@shared/utils/logger';
import { useAccommodationsContext } from '../../context/useAccommodationsContext';
import { drawerOverrides } from '@shared/components/ResponsiveDialog/ResponsiveDialog.styles';
import { DateMismatchWarning } from '@apps/admin/components/DateMismatchWarning';

type StayDatesDialogProps = Readonly<{
  isOpen: boolean;
  onClose: () => void;
}>;

export const StayDatesDialog = ({ isOpen, onClose }: StayDatesDialogProps) => {
  const { tq } = useTranslation('accommodationsAdmin');
  const { toast } = useToast();
  const { eventInfo } = useEventInfo();
  const finalizedDateString = eventInfo?.finalizedDate?.dateString;
  const { travelSettings } = useAccommodationsContext();
  const isMobile = useIsMobileScreen();

  const [checkInDate, setCheckInDate] = useState<Date | null>(null);
  const [checkOutDate, setCheckOutDate] = useState<Date | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const [updateTravelSettings] = useUpdateTravelSettingsMutation();

  const [today, setToday] = useState(() => startOfDay(new Date()));

  const showDateMismatchWarning = useMemo(() => isEventDateOutsideStayRange(checkInDate, checkOutDate, finalizedDateString), [checkInDate, checkOutDate, finalizedDateString]);

  // Sync local state from context when dialog opens
  useEffect(() => {
    if (isOpen) {
      setToday(startOfDay(new Date()));
      const parsedCheckIn = travelSettings?.checkInDate ? createDateFromUnformattedString(travelSettings.checkInDate) : null;
      const parsedCheckOut = travelSettings?.checkOutDate ? createDateFromUnformattedString(travelSettings.checkOutDate) : null;
      setCheckInDate(parsedCheckIn);
      setCheckOutDate(parsedCheckOut);
    }
  }, [isOpen, travelSettings?.checkInDate, travelSettings?.checkOutDate]);

  const handleCheckInChange = (date: Date | undefined) => {
    const newCheckIn = date ?? null;
    setCheckInDate(newCheckIn);
    if (newCheckIn && checkOutDate && newCheckIn >= checkOutDate) {
      setCheckOutDate(addDays(newCheckIn, 1));
    }
  };

  const handleSave = async () => {
    if (!eventInfo?.eventId) return;

    setIsSaving(true);
    try {
      await updateTravelSettings({
        variables: {
          payload: {
            eventId: eventInfo.eventId,
            checkInDate: checkInDate ? getLocaleDateString(checkInDate) : null,
            checkOutDate: checkOutDate ? getLocaleDateString(checkOutDate) : null,
          },
        },
        refetchQueries: ['AccommodationHotels'],
      });
      onClose();
    } catch (err) {
      addError(err, { context: 'StayDatesDialog', eventId: eventInfo.eventId });
      toast(tq`Error saving dates`, { icon: <WarningTriangle /> });
    } finally {
      setIsSaving(false);
    }
  };

  const content = (
    <Flex
      flexDirection="column"
      gap={6}
      padding={7}
    >
      <Flex
        justifyContent="space-between"
        alignItems="center"
      >
        <TextV2 typographyVariant="hed4">{tq`Suggested Stay Dates`}</TextV2>
        <CloseButton
          shape="rounded"
          size="md"
          onClick={onClose}
          aria-label={tq`Close`}
        />
      </Flex>
      <TextV2
        typographyVariant="label2"
        color="mono10"
      >
        {tq`Help guests book hotels with the right dates from the start.`}
      </TextV2>
      <Flex
        flexDirection="column"
        gap={5}
      >
        <FormControl label={tq`Check-In Date`}>
          <DatePickerInput
            mask={null}
            endElement={null}
            isReadOnly
            value={checkInDate}
            isDisabled={isSaving}
            enableCloseOnSelect
            placeholder={tq`Add check-in date`}
            dateFormat="EEEE, MMMM do, yyyy"
            onFocus={e => e.stopPropagation()}
            onChange={date => handleCheckInChange(date as Date | undefined)}
            minDate={today}
          />
        </FormControl>
        <FormControl label={tq`Check-Out Date`}>
          <DatePickerInput
            mask={null}
            endElement={null}
            isReadOnly
            value={checkOutDate}
            isDisabled={isSaving}
            enableCloseOnSelect
            placeholder={tq`Add check-out date`}
            dateFormat="EEEE, MMMM do, yyyy"
            onFocus={e => e.stopPropagation()}
            onChange={date => setCheckOutDate((date as Date | undefined) ?? null)}
            minDate={checkInDate ? max([addDays(checkInDate, 1), addDays(today, 1)]) : addDays(today, 1)}
          />
        </FormControl>
        {showDateMismatchWarning && <DateMismatchWarning message={tq`Your event date doesn't fall within these hotel dates. Guests may book for the wrong dates.`} />}
      </Flex>
      <ButtonV2
        intent="neutral"
        variant="solid"
        onClick={handleSave}
        loading={isSaving}
        disabled={isSaving || !checkInDate || !checkOutDate}
        width="100%"
      >
        {tq`Save`}
      </ButtonV2>
    </Flex>
  );

  if (isMobile) {
    return (
      <DrawerV2
        isOpen={isOpen}
        onClose={onClose}
        overrides={drawerOverrides}
        scrollBehavior="inside"
        disableFocusLockRemoveScroll
        disableAutoFocus
        anchor="bottom"
      >
        {content}
      </DrawerV2>
    );
  }

  return (
    <DialogV2
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior="inside"
      size="md"
      disableAutoFocus
      disableFocusLockRemoveScroll
    >
      {content}
    </DialogV2>
  );
};
